import { useCallback, useEffect, useMemo, useState } from "react";
import CaptureCamera from "./components/CaptureCamera";
import Count from "./pages/Count";
import Enter from "./pages/Enter";
import { fetchSession } from "./lib/captureClient";
import { sharedUploadQueue } from "./lib/uploadQueue";
import { linkBinding, linkNeedsFreshGrant, linkSupersedes, productDiffers, sessionBinding } from "./lib/linkDecision";
import { applyReturnPath, safeReturnUrl } from "./lib/returnUrl";

// The whole application: one camera and one question.
//
// THE QUESTION IS ASKED FIRST, ALWAYS. Before anything renders we ask the
// backend who this handset is. That answer decides everything, including which
// shop's pile is on screen, and it is deliberately not something this app can
// guess, cache or be told. There is no shop in the URL, none in localStorage
// and none in the build: this page learns a tenant only from a server that read
// it out of a grant. A dead session therefore cannot degrade into "show
// something anyway".
//
// THERE IS ONE CAMERA DESIGN (components/CaptureCamera.jsx) and every kind of
// link lands on it: Quick scan (m=quick), a link bound to a product, a barcode
// or a new item (the admin's "Capture in browser"), and Bulk scan, which is what
// an unbound link opens. Count (m=count) is a separate job with its own screen.
// The camera must NOT unmount when anything around it changes, so there is no
// router: the session decides the component and that is all.

export default function App() {
  // `null` means "we have not asked yet" and is NOT the same as "no session".
  // Rendering the door on the first frame, before the answer arrives, would
  // flash "ask your admin for a link" at everyone with a perfectly good one.
  const [session, setSession] = useState(null);
  const [checked, setChecked] = useState(false);
  // THE WAY BACK TO SHOPIFY, captured on the FIRST render. The query string
  // is replaced with "/" as soon as the token is spent (onEntered below, and
  // the signed-in branch), and every later render reads the stripped URL — so
  // `r` has to be taken now or never. `p` rides with it: the link's product,
  // compared against the grant's before `r` is believed — and `b`, the link's
  // barcode, for a Quick Scan link minted before the barcode had a product.
  const [linkReturn] = useState(() => {
    if (typeof window === "undefined") return { r: null, p: null, b: null, n: false, g: null, desk: false };
    const q = new URLSearchParams(window.location.search);
    // `d=1`: opened from the desktop QR. A hint only; kept for the tab's life as the query is stripped.
    let desk = q.get("d") === "1";
    try {
      if (desk) window.sessionStorage.setItem("cap.desk", "1");
      else desk = window.sessionStorage.getItem("cap.desk") === "1";
    } catch {
      /* private mode: the hint lasts until a reload */
    }
    // `rp`: the page the tap came from, applied to `r` only if it validates.
    return { r: applyReturnPath(q.get("r"), q.get("rp")), p: q.get("p"), b: q.get("b"), n: q.get("n") === "1", g: q.get("g"), bulk: q.get("v") === "bulk", desk };
  });

  // A Bulk scan opened from Cosmos goes back to the Shopify page it came from,
  // never to the captures home. Kept for the tab's life, checked against the
  // session's shop on every read.
  const bulkReturnUrl = useMemo(() => {
    const KEY = "cap.returnUrl.bulk";
    let raw = null;
    try {
      if (linkReturn.bulk && linkReturn.r) window.sessionStorage.setItem(KEY, linkReturn.r);
      raw = window.sessionStorage.getItem(KEY);
    } catch {
      raw = linkReturn.bulk ? linkReturn.r : null;
    }
    return session?.shop ? safeReturnUrl(raw, session.shop) : null;
  }, [linkReturn, session?.shop]);

  const check = useCallback(async () => {
    try {
      setSession(await fetchSession());
    } catch {
      // A network failure is not an expired session, and must not be reported
      // as one — telling someone to go and get a new link when the wifi
      // dropped sends them across the shop for nothing.
      setSession(null);
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  // Any screen that learns the session is dead says so here, and the door
  // takes over. One place decides, so a 401 during an upload and a 401 during
  // a page load cannot end up showing two different things.
  const onExpired = useCallback(() => {
    setSession(null);
  }, []);

  // Validated against the SESSION's shop and product (or barcode), never the
  // link's `s`, `p` and `b` alone — all of them are equally editable.
  // Persisted per binding in sessionStorage so a reload of this tab, which
  // arrives with no query string, still has its way back; re-validated on the
  // way out of storage too. Storage can throw (private mode), and then a
  // reload simply loses the button, which is today's behaviour.
  //
  // A BARCODE session is checked against `b` and stored under the barcode,
  // not the product: the grant is pinned to a product after its first commit,
  // and a key that moved with it would lose the way back on the next reload.
  const returnUrl = useMemo(() => {
    const barcode = session?.barcode;
    const productId = session?.productId;
    const newGrant = session?.newItem ? session.grantId : null;
    // A quick session is keyed on its grant: it has no binding of its own, and
    // the pin must not move the key.
    const quickGrant = session?.mode === "quick" ? session.grantId : null;
    if (!barcode && !productId && !newGrant && !quickGrant) return null;
    // A new-item session is keyed on its grant: its product is minted later.
    const key = quickGrant
      ? `cap.returnUrl.q.${quickGrant}`
      : newGrant
      ? `cap.returnUrl.g.${newGrant}`
      : barcode
        ? `cap.returnUrl.b.${barcode}`
        : `cap.returnUrl.${productId}`;
    // The same identity linkDecision compares, absent-as-none: a product
    // link's `r` must never be stored under a barcode session's key (or the
    // reverse) just because it carried no `b` (or no `p`).
    const linkIsForThisBinding = quickGrant
      ? linkReturn.p == null && linkReturn.b == null && !linkReturn.n && !linkReturn.g
      : linkBinding({ product: linkReturn.p, barcode: linkReturn.b, newItem: linkReturn.n, grant: linkReturn.g }) ===
      sessionBinding(session);
    if (linkReturn.r && linkIsForThisBinding) {
      const safe = safeReturnUrl(linkReturn.r, session.shop);
      if (safe) {
        try {
          window.sessionStorage.setItem(key, safe);
        } catch {
          /* private mode: the button lasts until a reload */
        }
        return safe;
      }
      console.warn("[capture] return link refused: not this store's admin");
    }
    let stored = null;
    try {
      stored = window.sessionStorage.getItem(key);
    } catch {
      /* private mode */
    }
    return safeReturnUrl(stored, session.shop);
  }, [session?.mode, session?.productId, session?.barcode, session?.newItem, session?.grantId, session?.shop, linkReturn]);

  // ⚠️ THE SESSION CHECK COMES FIRST, AND THE ORDER IS THE WHOLE POINT.
  //
  // An earlier version rendered <Enter> whenever `?t=` was present, before
  // knowing whether this handset was already signed in. So a phone that WAS
  // signed in, tapping "Open capture" again, spent a second token and opened a
  // second session — and the admin's list showed one phone as two devices.
  // Observed on the dev store within minutes: two live grants, one deviceId,
  // nineteen seconds apart.
  //
  // Waiting for `checked` costs one round trip on a link that would otherwise
  // redeem immediately. That is worth it: a token not spent is a token that
  // simply expires, and the alternative is a duplicate session every time.
  if (!checked) return <Splash />;

  const token = new URLSearchParams(window.location.search).get("t");
  if (token && !session) {
    return (
      <Enter
        token={token}
        onEntered={async () => {
          // Drop the token out of the address bar before anything else. It is
          // spent, but a spent token in a screenshot or a shared link is still
          // a question we should not have to answer.
          window.history.replaceState({}, "", "/");
          await check();
        }}
      />
    );
  }

  // Already signed in and carrying a link anyway.
  //
  // ⚠️ "REDUNDANT" MEANS SAME STORE AND SAME JOB. It used to mean neither.
  //
  // When there was one kind of link and everyone tested on one store, a token
  // arriving at a signed-in handset was genuinely redundant and spending it just
  // made a second session. Both of those assumptions have since failed, in the
  // same place, three weeks apart:
  //
  //   the job    a phone signed in to photograph tapped "Open stock count on
  //              this device", got the gallery, and no message. Fixed by `m`.
  //
  //   the store  a phone signed in to photograph for variety tapped "Open
  //              capture" inside KRYSTAL's admin, got variety's pile, and no
  //              message. `m` did not catch it because both links say
  //              photograph — the modes match, so the link looked redundant.
  //              The person then photographed a shelf into the wrong store's
  //              pile with nothing on screen contradicting them.
  //
  // So the question the branch asks is now "is this link for the session I am
  // already holding", and a link that differs in EITHER dimension wins. That is
  // the whole of "a button in a store's admin takes you to that store": the
  // link carries where it came from, and the newest tap decides.
  //
  // Both are HINTS and nothing more. They decide only whether this link is
  // worth redeeming; the shop and mode that govern the app still come from the
  // grant, read out of the database on every request. A tampered hint can make
  // us spend a token we did not need to. It cannot make a handset count, and it
  // cannot point one at a store the grant was not minted for.
  //
  // The comparison itself is in lib/linkDecision.js, with a test that
  // enumerates the cases — including the one that reads as an edge case and is
  // not: a missing hint is NOT a mismatch. Links minted before `s` existed
  // carry no shop, and treating absent as different would re-redeem every one
  // of them. It is four lines and it has been wrong twice; that is the bar for
  // pulling something out of a component.
  const params = new URLSearchParams(window.location.search);
  const wantedMode = params.get("m");
  const wantedShop = params.get("s");
  // `p`: the product a product link was minted for. A hint like the others —
  // whether the session IS product-bound comes from fetchSession, never this.
  const wantedProduct = params.get("p");
  // `b`: the barcode a Quick Scan link was minted for, when it had no product
  // yet. Compared as a binding of its own — see lib/linkDecision.js.
  const wantedBarcode = params.get("b");
  // `n` and `g`: a no-barcode (new item) link and its grant id.
  const wantedNew = params.get("n") === "1";
  const wantedGrant = params.get("g");
  const wanted = {
    newItem: wantedNew,
    grant: wantedGrant,
    mode: wantedMode,
    shop: wantedShop,
    product: wantedProduct,
    barcode: wantedBarcode,
  };
  const wrongShop = Boolean(wantedShop) && wantedShop !== session?.shop;
  // The same hazard one level down: commit binds photos to the GRANT's
  // product, so a capture still queued when the grant changes product would
  // be filed against the new one (or, going back to the shelf, refused).
  //
  // And for ANY bound link, even one for the binding already held: it is
  // always redeemed (see linkSupersedes), so a capture still queued would
  // commit under the NEW grant and be reported to the admin polling it as
  // photos taken for that link.
  const wrongProduct = productDiffers(session, wanted) || linkNeedsFreshGrant(wanted);
  if (token && session && linkSupersedes(session, wanted)) {
    // ⚠️ FINISH THE OLD STORE'S UPLOADS BEFORE ENTERING THE NEW ONE.
    //
    // The queue is a module singleton that deliberately outlives every screen,
    // and redeeming happens inside this same page — so a capture queued for
    // variety and still in flight would be COMMITTED WITH KRYSTAL'S COOKIE.
    // The backend resolves the shop from the session on every request, exactly
    // as it should, which is what makes this dangerous rather than merely
    // untidy: the photographs land in the new store's pile and nothing anywhere
    // records that they were shot in the old one.
    //
    // Waiting is the only option that loses nothing. It is also nearly always
    // instant — an upload takes seconds and this is a link someone has just
    // tapped in an admin on another screen.
    if ((wrongShop || wrongProduct) && sharedUploadQueue().outstanding() > 0) {
      return (
        <Switching
          to={wrongShop ? wantedShop : null}
          from={session.shop}
          onDrained={check}
        />
      );
    }
    return (
      <Enter
        token={token}
        // What the link PROMISED, so Enter's "are we already in?" fallback can
        // tell "this device already holds the session you asked for" from "this
        // device holds a different one" — see the note there. Without it, a
        // Krystal link that arrives a second too late puts the person straight
        // back into variety with no message, which is the same complaint by
        // another route.
        expect={{ shop: wantedShop, mode: wantedMode, product: wantedProduct, barcode: wantedBarcode, newItem: wantedNew, grant: wantedGrant }}
        onEntered={async () => {
          window.history.replaceState({}, "", "/");
          await check();
        }}
      />
    );
  }

  if (token && session) {
    window.history.replaceState({}, "", "/");
  }

  if (!session) return <Door />;

  // WHICH APP THIS HANDSET IS, decided by the grant and nowhere else.
  //
  // The session says `mode`, read out of the database on every request. There
  // is no toggle, no URL and no stored preference that reaches this — a phone
  // handed out to photograph cannot become one that writes stock, and the
  // backend refuses the counting endpoints for it even if this branch were
  // wrong. Two independent statements of the same rule, deliberately.
  //
  // Counting owns its own screen and its own history; it has no camera to keep alive.
  if (session.mode === "count") {
    return <Count shop={session.shop} onExpired={onExpired} />;
  }

  // QUICK SCAN LINK: one item, picked on the phone. Ahead of the bound branch
  // and keyed on a constant, because the first commit pins the grant to the
  // item and a later session read then carries a productId — which must neither
  // remount the camera nor route it to the bound flow.
  if (session.mode === "quick") {
    return <CaptureCamera key="quick" quick={{ returnUrl, grantId: session.grantId, desk: linkReturn.desk }} trainingShop={session.training ? session.shop : null} trainingDailyMb={session.trainingDailyMb} />;
  }

  // BOUND TO ONE PRODUCT, OR ONE BARCODE: straight to the camera, no
  // scanner.
  //
  // Decided by the session's productId / barcode — the grant — not by `p` or
  // `b`. No onBack: the way out is back to Shopify — the Done state's link when
  // `r` survived validation, its sentence when it did not.
  //
  // ⚠️ KEYED ON THE BINDING, NOT ON productId. A barcode grant is pinned to a
  // product server-side by its first commit, so any later session read (a
  // Switching drain, an Enter fallback) comes back with a productId it did not
  // have before. Keyed on productId, that would REMOUNT the camera mid-session
  // — dropping the deck and the stream — for what is the same grant.
  // sessionBinding() is the barcode for the whole life of a barcode session,
  // and still changes on a genuine switch from A to B, which is what should
  // start a fresh deck. Nothing in the preset flow re-fetches the session after
  // a commit today; this keeps it safe if something ever does.
  if (session.productId || session.barcode || session.newItem) {
    const preset = session.newItem
      ? {
          // A new item: the server mints it on the first commit and pins the
          // grant. Until then there is no product; after, none is needed here.
          productId: null,
          barcode: session.barcode || null,
          newItem: true,
          productCode: session.productCode || null,
          productTitle: null,
          returnUrl,
        }
      : session.barcode
      ? {
          // The barcode wins even after the pin: the commit sends nothing that
          // names a product, and the screen names what was scanned.
          productId: null,
          barcode: session.barcode,
          productTitle: null,
          returnUrl,
        }
      : {
          productId: session.productId,
          barcode: null,
          productTitle: session.productTitle,
          returnUrl,
        };
    // The bound item as an item on file: its title, code and pictures (pack shot, then the
    // captured photos, the Shopify image as the fallback) come from the session.
    if (session.productId && !session.newItem && !session.barcode) {
      preset.candidate = session.candidate || {
        productId: session.productId,
        title: session.productTitle || null,
        code: session.productCode || null,
        images: [],
        photoCount: 0,
      };
    }
    return <CaptureCamera key={sessionBinding(session)} preset={preset} trainingShop={session.training ? session.shop : null} trainingDailyMb={session.trainingDailyMb} />;
  }

  // Everything else is Bulk scan: the unbound link's camera. Opened from Cosmos it
  // goes back to the Shopify page it came from (bulkReturnUrl), else it just closes.
  return <CaptureCamera bulkReturnUrl={bulkReturnUrl} desk={linkReturn.desk} trainingShop={session.training ? session.shop : null} trainingDailyMb={session.trainingDailyMb} />;
}

/** Deliberately almost nothing: this is on screen for one network round trip. */
function Splash() {
  return <div className="cap-boot" aria-busy="true" />;
}

/** A store name, without the myshopify noise. */
function shopName(domain) {
  return String(domain || "").replace(/\.myshopify\.com$/, "");
}

/**
 * Holding the door while the last store's uploads land.
 *
 * On screen only when someone taps another store's (or product's) link
 * mid-upload, which in practice means for a second or two. `to` is null when
 * the store is the same and only the product changes. The token in the address
 * bar is NOT stripped while this is showing — it has not been spent, and it
 * stays good for the rest of its five minutes.
 *
 * Polled rather than subscribed on purpose. Subscribing means taking the
 * queue's single handler slot, and the screen this replaced may still be
 * holding it; a half-second poll cannot lose a race with anything.
 */
function Switching({ to, from, onDrained }) {
  const [state, setState] = useState(() => sharedUploadQueue().summary());

  useEffect(() => {
    const q = sharedUploadQueue();
    const tick = setInterval(() => {
      const next = q.summary();
      setState(next);
      // Re-checking the session is what takes this screen down: `check` flips
      // no state of its own here, but App re-renders and the branch above is
      // re-evaluated with an empty queue, so <Enter> takes over.
      if (q.outstanding() === 0) {
        clearInterval(tick);
        void onDrained();
      }
    }, 500);
    return () => clearInterval(tick);
  }, [onDrained]);

  const stuck = state.failed > 0;
  const n = state.pending;

  return (
    <div className="cap-door" aria-busy={!stuck}>
      <h1>Finishing up in {shopName(from)}</h1>
      <p>
        {n} capture{n === 1 ? "" : "s"}{" "}
        {stuck ? "could not upload" : "still uploading"}.{" "}
        {to ? shopName(to) : "The new link"} will open as soon as{" "}
        {n === 1 ? "it is" : "they are"} done.
      </p>
      {stuck ? (
        <>
          <p className="cap-door-note">
            Your photographs are safe on this device. Check the signal and try
            again.
          </p>
          <div className="cap-switch-actions">
            <button type="button" onClick={() => sharedUploadQueue().retry()}>
              Try again
            </button>
            <button
              type="button"
              className="cap-switch-discard"
              onClick={() => {
                // The one path here that loses bytes, so it asks — the same
                // contract discardFailed states in uploadQueue.js. One item,
                // the stuck one, never the whole queue.
                if (
                  window.confirm(
                    `Discard the capture that will not upload? Its photographs ` +
                      `are lost. The rest of the queue keeps trying.`,
                  )
                ) {
                  sharedUploadQueue().discardFailed();
                }
              }}
            >
              Discard it
            </button>
          </div>
        </>
      ) : (
        <p className="cap-door-note">
          {to ? (
            <>
              Leaving now would file {shopName(from)}&rsquo;s photographs into{" "}
              {shopName(to)}, so this waits instead.
            </>
          ) : (
            <>
              Leaving now would file these photographs against the wrong
              product, so this waits instead.
            </>
          )}{" "}
          It is usually a few seconds.
        </p>
      )}
    </div>
  );
}

/**
 * No session. The only screen in this app that cannot be fixed from this app.
 *
 * It says what to do rather than what went wrong, because the cause is never
 * something the person holding the phone can act on: the link expired, someone
 * revoked the handset, the shift ran past twelve hours, or capture was switched
 * off for this shop. All four have the same remedy.
 */
function Door() {
  return (
    <div className="cap-door">
      <h1>Not signed in</h1>
      <p>
        This device needs a link from your store admin before it can capture.
      </p>
      <ol>
        <li>Open your Shopify admin</li>
        <li>
          Go to <b>Product Enricher &rarr; Capture</b>
        </li>
        <li>Tap <b>Open capture on this device</b></li>
      </ol>
      <p className="cap-door-note">
        Links last five minutes and work once. A signed-in device stays signed
        in for up to twelve hours.
      </p>
    </div>
  );
}
